import { ReactNode, useEffect, useRef, useState } from "react";
import type { Message } from "@ag-ui/core";
import { cn } from "~/lib/utils";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import type { ArtifactPreview } from "~/services/artifacts";
import { ConversationProvider } from "./ConversationContext";
import { ConversationTurns } from "./ConversationTurns";
import { SharedArtifactLineagePanel } from "./SharedArtifactLineagePanel";
import { ToolUIProvider } from "./ToolUIContext";
import { ArtifactCardActionsProvider } from "./artifacts/ArtifactCardActions";
import { TOOL_UI_REGISTRY } from "./tool-ui-registry";
import { useAnchoredScroll } from "./useAnchoredScroll";
import { useTurns } from "./useTurns";

export interface ReadOnlyConversationData {
  display_name: string;
  owner_display_name: string | null;
  created_at: string;
  messages: Message[];
}

export interface ReadOnlyConversationViewProps {
  conversationId: string | null;
  // Set on share-token views so artifact renderers can hit the token-scoped
  // previews endpoint. Exactly one of conversationId / shareToken is set.
  shareToken?: string | null;
  // Set on admin reviewer views so artifact renderers hit the admin previews
  // endpoint — the owner one refuses a cross-user read.
  isAdmin?: boolean;
  fetcher: (signal: AbortSignal) => Promise<ReadOnlyConversationData | null>;
  bannerSubtitle?: string;
  // When set, replaces the entire subtitle line (ownerVerb + owner + bannerSubtitle).
  bannerSubtitleContent?: string;
  ownerVerb?: string;
  notFoundTitle?: string;
  notFoundBody?: string;
  rightPanel?: ReactNode;
  // Strip between the app header and the conversation, spanning the full window
  // width above both the transcript and the right panel. Owns its own padding
  // and bottom border.
  topBand?: ReactNode;
  readOnlyFeedback?: boolean;
  // Omits the session name from the banner — for callers that already show
  // it elsewhere (e.g. the in-project featured route, via breadcrumbs),
  // where repeating it here would be redundant.
  hideTitle?: boolean;
}

export const ReadOnlyConversationView = ({
  conversationId,
  shareToken = null,
  isAdmin = false,
  fetcher,
  bannerSubtitle = "Read-only view.",
  bannerSubtitleContent,
  ownerVerb = "Shared by",
  notFoundTitle = "This conversation isn't available",
  notFoundBody = "It may have been removed, or it never existed.",
  rightPanel,
  topBand,
  readOnlyFeedback = false,
  hideTitle = false,
}: ReadOnlyConversationViewProps) => {
  const [view, setView] = useState<ReadOnlyConversationData | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  // The artifact whose lineage panel is open, on a share-token view.
  const [lineageArtifact, setLineageArtifact] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const next = await fetcher(controller.signal);
        if (controller.signal.aborted) return;
        setView(next);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err);
      }
    })();
    return () => controller.abort();
  }, [fetcher]);

  if (error) {
    return <ResourceLoadError error={error} resourceLabel="conversation" />;
  }

  if (view === undefined) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        role="status"
        aria-busy="true"
        aria-label="Loading conversation"
      />
    );
  }

  if (view === null) {
    return <StatusMessage title={notFoundTitle} body={notFoundBody} />;
  }

  // A share-token view opens a card's lineage beside the transcript; other
  // read-only views keep the cards' inline Lineage tab.
  const resolveCardActions = shareToken
    ? (preview: ArtifactPreview) => {
        const name = preview.artifact_name;
        return {
          lineage:
            name === null
              ? null
              : {
                  isOpen: lineageArtifact === name,
                  open: () => setLineageArtifact((current) => (current === name ? null : name)),
                },
          share: null,
        };
      }
    : null;

  return (
    <ConversationProvider conversationId={conversationId} shareToken={shareToken} isAdmin={isAdmin}>
      <ArtifactCardActionsProvider resolve={resolveCardActions}>
        <ToolUIProvider value={TOOL_UI_REGISTRY}>
          <div className="flex h-full flex-col overflow-hidden">
            {topBand}
            {/* The row and the body column inside it stay mounted whether or not
              the band and panel slots are filled. Making either wrapper
              conditional on its slot would reconcile the transcript against a
              different element as that slot appears, remounting it and losing
              scroll position and artifact frames. An absent slot renders nothing
              and takes no space. */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <ReadOnlyConversationBody
                  view={view}
                  conversationId={conversationId}
                  bannerSubtitle={bannerSubtitle}
                  bannerSubtitleContent={bannerSubtitleContent}
                  ownerVerb={ownerVerb}
                  readOnlyFeedback={readOnlyFeedback}
                  hideTitle={hideTitle}
                />
              </div>
              {rightPanel}
              {shareToken && lineageArtifact && (
                <SharedArtifactLineagePanel
                  shareToken={shareToken}
                  artifactName={lineageArtifact}
                  onClose={() => setLineageArtifact(null)}
                />
              )}
            </div>
          </div>
        </ToolUIProvider>
      </ArtifactCardActionsProvider>
    </ConversationProvider>
  );
};

interface ReadOnlyConversationBodyProps {
  view: ReadOnlyConversationData;
  conversationId: string | null;
  bannerSubtitle: string;
  bannerSubtitleContent?: string;
  ownerVerb: string;
  readOnlyFeedback: boolean;
  hideTitle: boolean;
}

const ReadOnlyConversationBody = ({
  view,
  conversationId,
  bannerSubtitle,
  bannerSubtitleContent,
  ownerVerb,
  readOnlyFeedback,
  hideTitle,
}: ReadOnlyConversationBodyProps) => {
  const turns = useTurns(view.messages, false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { highlightedTurnId } = useAnchoredScroll(scrollRef);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto pt-6">
        <div className="mx-auto flex max-w-[60rem] flex-col gap-4 px-6 pb-8">
          <header className="rounded-xl border border-border bg-muted/30 px-4 py-3">
            {!hideTitle && (
              <h1 className="truncate text-base font-medium text-foreground">
                {view.display_name}
              </h1>
            )}
            <p className={cn("text-xs text-muted-foreground", !hideTitle && "mt-1")}>
              {bannerSubtitleContent ??
                `${view.owner_display_name ? `${ownerVerb} ${view.owner_display_name} · ` : ""}${bannerSubtitle}`}
            </p>
          </header>
          <ol className="flex flex-col gap-4" role="log">
            <ConversationTurns
              turns={turns}
              conversationId={conversationId ?? ""}
              readOnlyFeedback={readOnlyFeedback}
              highlightedTurnId={highlightedTurnId}
              showTurnLinks
            />
          </ol>
        </div>
      </div>
    </div>
  );
};
